import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Bold, Italic, Heading2, List, ListOrdered, Image as ImageIcon, Paperclip } from 'lucide-react';
import { notifications } from '../../utils/toastNotifications';

const MAX_FILE_SIZE = 100 * 1024 * 1024;

function checkFileSize(file: File): boolean {
  if (file.size > MAX_FILE_SIZE) {
    notifications.error('File exceeds the 100 MB limit.');
    return false;
  }
  return true;
}

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  readOnly?: boolean;
  onImageUpload: (file: File) => Promise<string>;
  onFileUpload?: (file: File) => Promise<void>;
}

const ToolbarButton: React.FC<{
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}> = ({ active, onClick, title, children }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => {
      e.preventDefault();
      onClick();
    }}
    className={`p-1.5 rounded text-sm transition-colors ${
      active ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
    }`}
  >
    {children}
  </button>
);

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  readOnly = false,
  onImageUpload,
  onFileUpload
}) => {
  const imageInputRef = React.useRef<HTMLInputElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const editor = useEditor(
    {
      extensions: [StarterKit, Image.configure({ inline: false, allowBase64: false })],
      content: value,
      editable: !readOnly,
      onUpdate: ({ editor }) => {
        onChange(editor.getHTML());
      }
    },
    [readOnly]
  );

  // Sync external value changes (e.g. when modal resets)
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (current !== value) {
      editor.commands.setContent(value);
    }
  }, [value]);

  const handleImageFile = async (file: File) => {
    if (!editor) return;
    const url = await onImageUpload(file);
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  if (readOnly) {
    return (
      <div
        className="prose prose-sm max-w-none p-3 border border-gray-200 rounded min-h-[80px] text-sm text-gray-700 [&_img]:hidden"
        dangerouslySetInnerHTML={{ __html: value || '' }}
      />
    );
  }

  return (
    <div className="border border-gray-300 rounded overflow-hidden focus-within:border-blue-500 transition-colors">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1 border-b border-gray-200 bg-gray-50 flex-wrap">
        <ToolbarButton
          active={editor?.isActive('bold')}
          onClick={() => editor?.chain().focus().toggleBold().run()}
          title="Bold"
        >
          <Bold size={14} />
        </ToolbarButton>

        <ToolbarButton
          active={editor?.isActive('italic')}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          title="Italic"
        >
          <Italic size={14} />
        </ToolbarButton>

        <div className="w-px h-4 bg-gray-300 mx-1" />

        <ToolbarButton
          active={editor?.isActive('heading', { level: 2 })}
          onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
          title="Heading"
        >
          <Heading2 size={14} />
        </ToolbarButton>

        <div className="w-px h-4 bg-gray-300 mx-1" />

        <ToolbarButton
          active={editor?.isActive('bulletList')}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          title="Bullet list"
        >
          <List size={14} />
        </ToolbarButton>

        <ToolbarButton
          active={editor?.isActive('orderedList')}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          title="Ordered list"
        >
          <ListOrdered size={14} />
        </ToolbarButton>

        <div className="w-px h-4 bg-gray-300 mx-1" />

        <ToolbarButton onClick={() => imageInputRef.current?.click()} title="Insert image">
          <ImageIcon size={14} />
        </ToolbarButton>

        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (file && checkFileSize(file)) await handleImageFile(file);
            e.target.value = '';
          }}
        />

        {onFileUpload && (
          <>
            <ToolbarButton onClick={() => fileInputRef.current?.click()} title="Attach file">
              <Paperclip size={14} />
            </ToolbarButton>

            <input
              ref={fileInputRef}
              type="file"
              accept="video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              className="sr-only"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file && checkFileSize(file)) await onFileUpload(file);
                e.target.value = '';
              }}
            />
          </>
        )}
      </div>

      {/* Editor content */}
      <div className="[&_.tiptap]:outline-none [&_.tiptap]:min-h-[96px] [&_.tiptap]:p-3 [&_.tiptap]:text-sm [&_.tiptap_img]:hidden [&_.tiptap_ul]:list-disc [&_.tiptap_ul]:pl-5 [&_.tiptap_ol]:list-decimal [&_.tiptap_ol]:pl-5 [&_.tiptap_h2]:text-base [&_.tiptap_h2]:font-semibold [&_.tiptap_strong]:font-semibold [&_.tiptap_em]:italic">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default RichTextEditor;
